<template>
    <v-dialog v-model="dialog" persistent max-width="500">
        <template v-slot:activator="{ on }">
            <v-list-item v-on="on" @click.stop.prevent>
                <v-list-item-icon>
                    <v-icon>mdi-swap-horizontal</v-icon>
                </v-list-item-icon>
                <v-list-item-title>Change role</v-list-item-title>
            </v-list-item>
        </template>

        <v-card flat>
            <v-card-title class="flex-nowrap align-start secondary--text">
                <div>
                    <div>Change role</div>
                    <div class="text-subtitle-2 font-weight-regular">{{ instanceName }}</div>
                </div>
                <v-spacer />
                <v-btn icon @click="dialog = false"><v-icon>clear</v-icon></v-btn>
            </v-card-title>

            <v-card-text class="mt-4">
                <v-card flat class="grey lighten-4">
                    <v-card-title class="flex-nowrap align-start">
                        {{ userName }}
                    </v-card-title>
                    <v-card-subtitle>
                        {{ email }}
                    </v-card-subtitle>
                </v-card>
                <v-radio-group v-model="selectedRole">
                    <v-card
                        v-for="(availableRole, index) in availableRoles"
                        :key="availableRole"
                        outlined
                        :class="{ 'mb-2': index < availableRoles.length - 1, 'selected-border': selectedRole === availableRole }">
                        <v-radio :label="instanceRole(availableRole).label" :value="availableRole" class="pa-4">
                            <template v-slot:label>
                                <v-icon left :color="selectedRole === availableRole ? 'primary' : undefined">
                                    {{ instanceRole(availableRole).icon }}
                                </v-icon>
                                <span :class="selectedRole === availableRole ? 'primary--text' : undefined">
                                    {{ capitalize(instanceRole(availableRole).label) }}
                                </span>
                            </template>
                        </v-radio>
                    </v-card>
                </v-radio-group>
            </v-card-text>

            <v-card-actions>
                <v-spacer />
                <v-btn text color="primary" :loading="saving" :disabled="selectedRole === role" @click="saveRole">Save</v-btn>
            </v-card-actions>
        </v-card>
    </v-dialog>
</template>

<script>
import { capitalize } from 'lodash'
import { enumsData } from '@/mixins/enums'
import { instanceRole } from '@/utils'
import { createBackendErrorHandler } from '@/utils/errorMessages'
import * as instanceErrors from '@/modules/instance/errors'
import * as lifecycleErrors from '@/modules/snapshot/errors/lifecycle'
import * as permissionsErrors from '@/utils/errors/permissions'

const { getBackendErrorMessage } = createBackendErrorHandler(instanceErrors, lifecycleErrors, permissionsErrors)

export default {
    name: 'InstanceUserRoleDialog',
    mixins: [enumsData],
    props: {
        ucid: {
            type: String,
            required: true
        },
        userName: {
            type: String,
            required: true
        },
        email: {
            type: String,
            required: true
        },
        instanceId: {
            type: String,
            required: true
        },
        instanceName: {
            type: String,
            required: true
        },
        role: {
            type: String,
            required: true
        }
    },
    data() {
        return {
            dialog: false,
            selectedRole: this.role,
            saving: false
        }
    },
    computed: {
        availableRoles() {
            return [this.roleTypes.INSTANCE_EDITOR, this.roleTypes.INSTANCE_VIEWER, this.roleTypes.INSTANCE_OBSERVER]
        }
    },
    watch: {
        dialog(open) {
            if (open) {
                this.selectedRole = this.role
            } else {
                this.$emit('close-menu')
            }
        }
    },
    methods: {
        capitalize,
        instanceRole,
        async saveRole() {
            this.saving = true
            try {
                await this.$axios.post(`/instances/${this.instanceId}/assign_role_for_conn`, {
                    ucid: Number(this.ucid),
                    role_name: this.selectedRole
                })
                this.$emit('role-updated')
                this.dialog = false
            } catch (error) {
                const snackBarText = getBackendErrorMessage(error, `There was a problem updating the user's role. Please try again.`)
                if (snackBarText) {
                    this.$store.dispatch('showSnackBar', {
                        snackBarText,
                        snackBarTimeout: 2000,
                        snackBarIcon: 'error'
                    })
                }
            } finally {
                this.saving = false
            }
        }
    }
}
</script>

<style scoped>
.selected-border {
    border-color: #3381ff !important;
}
</style>
